import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { QuestionnaireSectionOwnProps } from "../QuestionnaireSectionProps";
import { registerSection as registerSectionAction, registerSectionId, removeSection, updateQuestion as updateQuestionAction } from "../actions";
import { AnsweredQuestionWithId, QuestionnairesState, Section } from "../model";

export interface QuestionnaireContainerValues {
    questionnaireKey: string;
    readonly: boolean;
    sections: Section[];
    extraQuestionContent: { [key: string]: React.ReactNode } | undefined;
    registerSection: (sectionId: string) => void;
    deregisterSection: (sectionId: string) => void;
    updateSection: (
        sectionId: string,
        title: string,
        active: boolean | undefined,
        neverHide: boolean,
        index: number | undefined
    ) => void;
    updateQuestion: (payload: AnsweredQuestionWithId) => void;
}

const QuestionnaireContainerContext = React.createContext<QuestionnaireContainerValues | null>(null);

export const useQuestionnaireContainer = () => {
    const questionnaireContext = React.useContext(QuestionnaireContainerContext);
    if (questionnaireContext === null) { throw new Error("No QuestionnaireContainerProvider found"); }
    return questionnaireContext;
};

export interface QuestionnaireContainerForSection {
    questionnaireKey: string;
    readonly: boolean;
    extraQuestionContent: { [key: string]: React.ReactNode } | undefined;
    section: Section;
    updateQuestion: (payload: Omit<AnsweredQuestionWithId, "sectionId">) => void;
}

export const useQuestionnaireContainerForSection = (id: string): QuestionnaireContainerForSection => {
    const { questionnaireKey, readonly, sections, updateQuestion: updateAnswer, extraQuestionContent } = useQuestionnaireContainer();
    const section = React.useMemo(() => sections.find(s => s.id === id) || {} as Section, [id, sections]);

    return {
        questionnaireKey,
        readonly,
        section,
        updateQuestion: React.useCallback(
            (payload: Omit<AnsweredQuestionWithId, "sectionId">) => updateAnswer({ ...payload, sectionId: id }),
            [id, updateAnswer]),
        extraQuestionContent
    };
};

export const useSectionWithQuestionnaireContainer = (
    { id, title, active, neverHide, index }: Pick<QuestionnaireSectionOwnProps, "id" | "title" | "active" | "neverHide" | "index">
): void => {
    const { registerSection, deregisterSection, updateSection } = useQuestionnaireContainer();

    React.useEffect(() => {
        updateSection?.(
            id,
            title,
            active,
            !!neverHide,
            index
        );
    }, [active, id, neverHide, updateSection, title, index]);

    React.useEffect(() => {
        registerSection?.(id);
        return () => deregisterSection?.(id);
    }, [deregisterSection, id, registerSection]);
};

const defaultSectionsArray: Section[] = [];

export const QuestionnaireContainerProvider: React.FC<
    Pick<QuestionnaireContainerValues, "questionnaireKey" | "readonly" | "extraQuestionContent">
> = ({ children, questionnaireKey, readonly, extraQuestionContent }) => {
    const dispatch = useDispatch();
    const sections = useSelector<
        { questionnaires: QuestionnairesState },
        Section[]
    >(state => state.questionnaires[questionnaireKey]?.sections ?? defaultSectionsArray);
    const updateSection = React.useCallback((sectionId, title, active, neverHide, index) => dispatch(registerSectionAction({
        sectionId,
        questionnaireKey,
        active,
        title,
        neverHide,
        index
    })), [dispatch, questionnaireKey]);
    const registerSection = React.useCallback((sectionId) => dispatch(registerSectionId({ sectionId, questionnaireKey })), [dispatch, questionnaireKey]);
    const deregisterSection = React.useCallback((sectionId) => dispatch(removeSection(questionnaireKey, sectionId)), [dispatch, questionnaireKey]);
    const updateQuestion = React.useCallback((payload: AnsweredQuestionWithId & { sectionId: string }) =>
        dispatch(updateQuestionAction({ sectionId: payload.sectionId, id: payload.id, question: payload, questionnaireKey })), [dispatch, questionnaireKey]);
    const containerValues = React.useMemo<QuestionnaireContainerValues>(() => ({
        questionnaireKey,
        readonly,
        extraQuestionContent,
        sections,
        updateSection,
        registerSection,
        deregisterSection,
        updateQuestion
    }), [deregisterSection, questionnaireKey, readonly, extraQuestionContent, registerSection, sections, updateSection, updateQuestion]);
    return (
        <QuestionnaireContainerContext.Provider value={containerValues}>
            {children}
        </QuestionnaireContainerContext.Provider>
    );
};
